import React from 'react';
import '../css/BannerCaptionOverlay.css';

/**
 * Validates whether HTML string from WYSIWYG editor has readable content.
 */
export const isCaptionEmpty = (html) => {
  if (!html || typeof html !== 'string') return true;
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  const lower = html.trim().toLowerCase();
  return (
    stripped.length === 0 ||
    lower === 'null' ||
    lower === 'undefined'
  );
};

/**
 * Normalizes backend position string to one of the 6 supported positioning keys:
 * - top_left     (top: 10%; left: 10%;)
 * - top_right    (top: 10%; right: 10%;)
 * - bottom_left  (bottom: 10%; left: 10%;)
 * - bottom_down  (bottom: 10%; left: 50%; transform: translateX(-50%);)
 * - bottom_right (bottom: 10%; right: 10%;)
 * - center       (top: 50%; left: 50%; transform: translate(-50%, -50%);)
 *
 * Fallback when null or unspecified defaults to 'bottom_left'.
 */
export const resolveBannerPosition = (item, defaultPosition = 'bottom_left') => {
  const raw =
    item?.position ||
    item?.title_position ||
    item?.text_position ||
    item?.align_position ||
    item?.banner_position ||
    item?.align;

  if (!raw || typeof raw !== 'string') {
    return defaultPosition;
  }

  const p = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');

  // Exact matches
  if (p === 'top_left' || p === 'topleft' || p === 'left_up') return 'top_left';
  if (p === 'top_right' || p === 'topright' || p === 'right_up') return 'top_right';
  if (p === 'bottom_left' || p === 'bottomleft' || p === 'left_down') return 'bottom_left';
  if (
    p === 'bottom_down' ||
    p === 'bottomdown' ||
    p === 'bottom_center' ||
    p === 'bottom_middle' ||
    p === 'center_down' ||
    p === 'middle_down'
  ) {
    return 'bottom_down';
  }
  if (p === 'bottom_right' || p === 'bottomright' || p === 'right_down') return 'bottom_right';
  if (p === 'center' || p === 'middle' || p === 'middle_up' || p === 'center_up') return 'center';

  // Substring matching
  if (p.includes('top') && p.includes('right')) return 'top_right';
  if (p.includes('top') && p.includes('left')) return 'top_left';
  if (p.includes('bottom') && p.includes('right')) return 'bottom_right';
  if (p.includes('bottom') && (p.includes('down') || p.includes('center') || p.includes('mid'))) return 'bottom_down';
  if (p.includes('bottom') && p.includes('left')) return 'bottom_left';
  if (p.includes('center') || p.includes('middle')) return 'center';

  return defaultPosition;
};

/**
 * Extracts the appropriate language title HTML string from the item.
 */
export const getCaptionHtml = (item, isRtl) => {
  if (!item) return '';
  const raw = isRtl
    ? (item.title_ar || item.title_en || '')
    : (item.title_en || item.title_ar || '');
  return isCaptionEmpty(raw) ? '' : raw.trim();
};

export default function BannerCaptionOverlay({
  item,
  isRtl = false,
  className = '',
  defaultPosition = 'bottom_left'
}) {
  if (!item) return null;

  const captionHtml = getCaptionHtml(item, isRtl);
  if (!captionHtml) return null;

  const position = resolveBannerPosition(item, defaultPosition);

  return (
    <div
      className={`banner-caption-overlay pos-${position} ${isRtl ? 'rtl' : 'ltr'} ${className}`.trim()}
      aria-label="Image caption overlay"
    >
      <div className="banner-caption-box">
        <div
          className="banner-caption-content"
          dangerouslySetInnerHTML={{ __html: captionHtml }}
        />
      </div>
    </div>
  );
}

